/*
 * Created on 02.05.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { Component, ContentChild, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../i18n/language.service';
import { FormInputComponent } from '../form-input/form-input.component';
import { I18N } from '../form.i18n';

/**
 * Das ist ein Wrapper um unsere &lt;common-form-input&gt; Komponente. Siehe {@link FormInputComponent}.
 * Das Input-Feld wird als &lt;ng-content&gt; übergeben. Beispiel:
 * <pre>
 * <common-form-field label="Name" class="required-class"
 *     [errorMessage]="{length: 'Muss eine Länge von mind 5 haben'}">
 *     <common-form-input formControlName="name" [dataToolTip]="'Name'">
 *     </common-form-input>
 * </common-form-field>
 * </pre>
 *
 * @author imam, thm
 */
@Component({
    selector: 'common-form-field',
    template: `
        <div class="form-group" [ngClass]="errorClass">
            <span *ngIf="control && errorMessages && errorMessages.length > 0">
                <div *ngFor="let msg of errorMessages">
                    <span>{{msg}}</span>
                </div>
            </span>

            <fieldset class="form-groupFieldset">
                <legend *ngIf="isClickable() && isLabelNotEmpty()"
                        [class]="class + ' fieldsetLegend'" [ngClass]="errorClass">{{label}}
                </legend>
                <label *ngIf="!isClickable() && isLabelNotEmpty()"
                       [class]="class" [ngClass]="errorClass">{{label}}</label>
                <ng-content></ng-content>
            </fieldset>
        </div>
    `
})
export class FormFieldComponent {
    /**
     * Das Label, das über dem Input-Feld angezeigt wird.
     */
    @Input()
    label: string;
    /**
     * Die Klasse, die an das Formfield angehangen wird. z.B. 'required-class'.
     */
    @Input()
    class: string = '';
    /**
     * I18n-Lookup-Objekt von Fehlerkey an FormControl zu angezeigter Fehlermeldung.
     * Beispiel:
     * <pre>
     * [errorMessage]="{length: 'Muss eine Länge von mind 5 haben'}"
     * </pre>
     */
    @Input()
    errorMessage: {[key: string]: string};
    /**
     * Die Form-Control. Wird aber auch automatisch über <code>@ContentChild</code> ermittelt.
     */
    @ContentChild(NgControl)
    control;
    uniqueId: string;
    messages: string[];
    subscription: any;
    i18n: any;
    cssclass;
    @ContentChild(FormInputComponent) fi;

    private readonly languageSubscription: Subscription;

    constructor(private languageService: LanguageService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    get errorClass() {
        if (this.control && !this.control.valid && this.errorMessages && this.errorMessages.length > 0
            && this.control.dirty) {
            this.cssclass = 'error-class';
        }
        if (this.control && this.control.control.status === 'PENDING') {
            return this.cssclass;
        }
        if (this.control && this.control.valid) {
            this.cssclass = '';
        }
        return this.cssclass;
    }

    get errorMessages() {
        if (this.control.dirty) {
            if (this.control.control.root._optionals && this.control.control.root._optionals
                .hasOwnProperty(this.control.name) && !this.control.control.root._optionals[this.control.name]) {
                return null;
            }
            if ((this.control.valid || !this.control.errors) && this.control.control.status !== 'PENDING') {
                this.messages = [];
                return this.messages;
            }
            if (this.control.control.status === 'PENDING') {
                return this.messages;
            }
            const c = this.control.errors;

            this.messages = [];
            Object.getOwnPropertyNames(c).forEach((val, _) => {
                let query;
                if (this.errorMessage && this.errorMessage[val]) {
                    query = this.errorMessage[val];
                } else {
                    query = this.i18n.formFieldComponent.globalMessages[val];
                }
                const argumente = c[val];
                if (query && query.match(/(?!\{)(\w+)(?=\})/g)) {
                    query = query.replace(/(\{)(\w+)(\})/g, argumente[query.match(/(?!\{)(\w+)(?=\})/g)]);
                }
                this.messages.push(query);
            });
            return this.messages;
        }
    }

    /**
     * Prüfung, ob der Typ des darzustellenden FormInput eine Checkbox oder ein Radio-Button ist.
     */
    isClickable() {
        return this.fi && this.fi.type && (this.fi.type === 'radio' || this.fi.type === 'checkbox');
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }

    /**
     * gibt an, ob das Label überhaupt gesetzt ist. Verhindert das Erstellen von leeren <label>-Tags.
     * @returns {boolean}
     */
    isLabelNotEmpty() {
        return this.label && this.label.length > 0;
    }
}
